// Rejected page — records a tech sent to the dead-end rejected state.
// One-click Resume moves it back to tech_review so you can fix and re-approve.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { RejectedRow } from "@shared/types";

const STORAGE_STORE_KEY = "smartrx-entry.currentStoreCode";
function getCurrentStoreCode(): string {
  return (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_STORE_KEY)) || "GV";
}

export function Rejected() {
  const [rows, setRows] = useState<RejectedRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState<number | null>(null);
  const storeCode = getCurrentStoreCode();

  function load(ctrl?: AbortController) {
    setLoading(true);
    apiGet<RejectedRow[]>(`/rejected?store=${storeCode}`, ctrl?.signal)
      .then((data) => { setRows(data); setError(null); })
      .catch((e) => { if (!ctrl?.signal.aborted) { setRows([]); setError(String(e)); } })
      .finally(() => { if (!ctrl?.signal.aborted) setLoading(false); });
  }

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl);
    return () => ctrl.abort();
  }, [storeCode]);

  async function resume(rxId: number) {
    if (!window.confirm("Resume this record? It will go back to tech review.")) return;
    setResuming(rxId);
    try {
      await apiPost(`/rx/${rxId}/resume`, {});
      load();
    } catch (e) {
      alert("Resume failed: " + String(e));
    } finally {
      setResuming(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-2xl font-semibold">Rejected</h2>
        <span className="text-ink-soft text-sm">{storeCode}</span>
      </div>
      <p className="text-ink-soft mb-6">
        {error
          ? <span className="text-bad">Rejected list unavailable: {error}</span>
          : `${rows.length} record${rows.length === 1 ? "" : "s"} rejected. Resume to send back for review.`}
      </p>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_1.1fr_1.1fr_140px_120px_1fr_100px] gap-3 px-4 py-2 text-xs uppercase tracking-wide text-ink-soft border-b border-border bg-pill">
          <span>#</span>
          <span>Patient</span>
          <span>Drug</span>
          <span>Rejected</span>
          <span>By</span>
          <span>Reason</span>
          <span></span>
        </div>

        {loading && rows.length === 0 && (
          <p className="text-ink-soft text-sm py-6 text-center">Loading…</p>
        )}
        {!loading && rows.length === 0 && !error && (
          <p className="text-ink-soft text-sm py-10 text-center">
            No rejected records for {storeCode}.
          </p>
        )}

        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div
              key={r.rxId}
              className="grid grid-cols-[64px_1.1fr_1.1fr_140px_120px_1fr_100px] gap-3 px-4 py-2.5 items-center text-sm hover:bg-pill"
            >
              <Link to={`/review/${r.rxId}`} className="text-ink-soft">#{r.rxId}</Link>
              <Link to={`/review/${r.rxId}`} className="truncate"><strong>{r.patientName ?? "—"}</strong></Link>
              <Link to={`/review/${r.rxId}`} className="truncate">{r.drug}</Link>
              <span className="text-ink-soft" title={r.rejectedAt}>{formatRelative(r.rejectedAt)}</span>
              <span className="truncate text-ink-soft">{r.rejectedByName ?? "—"}</span>
              <span className="truncate text-ink-soft text-xs" title={r.reason ?? ""}>{r.reason ?? "—"}</span>
              <button
                type="button"
                onClick={() => resume(r.rxId)}
                disabled={resuming === r.rxId}
                className="px-2 py-1 text-xs rounded border border-accent text-accent hover:bg-accent-soft disabled:opacity-40"
              >
                {resuming === r.rxId ? "…" : "Resume"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric" });
}
